import { useCallback, useMemo, useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Database,
  GitBranch,
  Pencil,
  Play,
  Plus,
  Search,
  Star,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { WebhookConfig } from '@/components/workflow-builder/panels/WebhookConfig';
import { WorkflowCanvas } from '@/components/workflow-builder/WorkflowCanvas';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useCreateWorkflow,
  useDeleteWorkflow,
  useRunWorkflow,
  useUpdateWorkflow,
  useWorkflows,
} from '@/hooks/useWorkflows';
import { extractErrorMessage } from '@/lib/utils';
import { useFeaturesStore } from '@/stores/features.store';
import { formatDate } from '@/utils/format';

import type { PostCallWorkflow, WorkflowEntity } from '@repo/shared-types';

// ---------------------------------------------------------------------------
// Schedule config helpers
// ---------------------------------------------------------------------------

type ScheduleMode = 'preset' | 'interval' | 'custom';

const PRESETS = [
  { label: 'Daily at 10:00 AM', cron: '0 10 * * *' },
  { label: 'Daily at 8:00 AM', cron: '0 8 * * *' },
  { label: 'Daily at 6:00 PM', cron: '0 18 * * *' },
  { label: 'Weekdays at 10:00 AM', cron: '0 10 * * 1-5' },
  { label: 'Weekly Monday 10:00 AM', cron: '0 10 * * 1' },
  { label: 'Weekly Sunday 6:00 PM', cron: '0 18 * * 0' },
  { label: 'Monthly 1st at 10:00 AM', cron: '0 10 1 * *' },
];

const INTERVAL_UNITS = [
  { label: 'Minutes', value: 'minutes' },
  { label: 'Hours', value: 'hours' },
  { label: 'Days', value: 'days' },
] as const;

const buildIntervalCron = (every: number, unit: string, atHour = 10, atMinute = 0): string => {
  switch (unit) {
    case 'minutes':
      return `*/${every} * * * *`;
    case 'hours':
      return `${atMinute} */${every} * * *`;
    case 'days':
      return `${atMinute} ${atHour} */${every} * *`;
    default:
      return `*/${every} * * * *`;
  }
};

const describeCron = (cron: string): string => {
  const preset = PRESETS.find((p) => p.cron === cron);
  if (preset) return preset.label;
  const parts = cron.split(' ');
  if (parts.length !== 5) return cron;
  const [min, hour, dom, , dow] = parts;
  if (min.startsWith('*/')) return `Every ${min.slice(2)} minute(s)`;
  if (hour.startsWith('*/')) return `Every ${hour.slice(2)} hour(s) at :${min.padStart(2, '0')}`;
  if (dom.startsWith('*/'))
    return `Every ${dom.slice(2)} day(s) at ${hour}:${min.padStart(2, '0')}`;
  if (dow !== '*') {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = days[Number(dow)] ?? dow;
    return `${dayName} at ${hour}:${min.padStart(2, '0')}`;
  }
  return cron;
};

const detectMode = (cron: string): ScheduleMode => {
  if (PRESETS.some((p) => p.cron === cron)) return 'preset';
  const parts = cron.split(' ');
  if (parts.some((p) => p.startsWith('*/'))) return 'interval';
  return 'custom';
};

interface ScheduleConfigProps {
  schedule: { cron: string; timezone: string; enabled: boolean };
  onChange: (s: { cron: string; timezone: string; enabled: boolean }) => void;
}

const ScheduleConfig = ({ schedule, onChange }: ScheduleConfigProps) => {
  const [mode, setMode] = useState<ScheduleMode>(() => detectMode(schedule.cron));
  const [intervalValue, setIntervalValue] = useState(5);
  const [intervalUnit, setIntervalUnit] = useState<string>('minutes');
  const [atHour, setAtHour] = useState(10);
  const [atMinute, setAtMinute] = useState(0);
  const [customCron, setCustomCron] = useState(schedule.cron);

  const inputClass =
    'border-input bg-background focus-visible:ring-ring rounded-md border px-2 py-1 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none';

  const handleModeChange = useCallback(
    (newMode: ScheduleMode) => {
      setMode(newMode);
      if (newMode === 'preset') {
        onChange({ ...schedule, cron: PRESETS[0].cron });
      } else if (newMode === 'interval') {
        onChange({
          ...schedule,
          cron: buildIntervalCron(intervalValue, intervalUnit, atHour, atMinute),
        });
      } else {
        onChange({ ...schedule, cron: customCron });
      }
    },
    [schedule, onChange, intervalValue, intervalUnit, atHour, atMinute, customCron],
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={mode}
        onChange={(e) => handleModeChange(e.target.value as ScheduleMode)}
        className={inputClass}
      >
        <option value="preset">Preset</option>
        <option value="interval">Every N...</option>
        <option value="custom">Cron expression</option>
      </select>

      {mode === 'preset' && (
        <select
          value={schedule.cron}
          onChange={(e) => onChange({ ...schedule, cron: e.target.value })}
          className={inputClass}
        >
          {PRESETS.map((p) => (
            <option key={p.cron} value={p.cron}>
              {p.label}
            </option>
          ))}
        </select>
      )}

      {mode === 'interval' && (
        <>
          <span className="text-muted-foreground text-xs">Every</span>
          <input
            type="number"
            min={1}
            max={999}
            value={intervalValue}
            onChange={(e) => {
              const v = Math.max(1, Number(e.target.value));
              setIntervalValue(v);
              onChange({ ...schedule, cron: buildIntervalCron(v, intervalUnit, atHour, atMinute) });
            }}
            className={`${inputClass} w-16`}
          />
          <select
            value={intervalUnit}
            onChange={(e) => {
              setIntervalUnit(e.target.value);
              onChange({
                ...schedule,
                cron: buildIntervalCron(intervalValue, e.target.value, atHour, atMinute),
              });
            }}
            className={inputClass}
          >
            {INTERVAL_UNITS.map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
          {(intervalUnit === 'hours' || intervalUnit === 'days') && (
            <>
              <span className="text-muted-foreground text-xs">at</span>
              {intervalUnit === 'days' && (
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={atHour}
                  onChange={(e) => {
                    const h = Math.min(23, Math.max(0, Number(e.target.value)));
                    setAtHour(h);
                    onChange({
                      ...schedule,
                      cron: buildIntervalCron(intervalValue, intervalUnit, h, atMinute),
                    });
                  }}
                  className={`${inputClass} w-14`}
                  title="Hour (0-23)"
                />
              )}
              <span className="text-muted-foreground text-xs">:</span>
              <input
                type="number"
                min={0}
                max={59}
                value={atMinute}
                onChange={(e) => {
                  const m = Math.min(59, Math.max(0, Number(e.target.value)));
                  setAtMinute(m);
                  onChange({
                    ...schedule,
                    cron: buildIntervalCron(intervalValue, intervalUnit, atHour, m),
                  });
                }}
                className={`${inputClass} w-14`}
                title="Minute (0-59)"
              />
            </>
          )}
        </>
      )}

      {mode === 'custom' && (
        <input
          value={customCron}
          onChange={(e) => {
            setCustomCron(e.target.value);
            onChange({ ...schedule, cron: e.target.value });
          }}
          placeholder="* * * * *"
          className={`${inputClass} w-36 font-mono`}
          title="Standard 5-field cron: min hour dom month dow"
        />
      )}

      <select
        value={schedule.timezone}
        onChange={(e) => onChange({ ...schedule, timezone: e.target.value })}
        className={inputClass}
      >
        <option value="Europe/London">London</option>
        <option value="UTC">UTC</option>
        <option value="America/New_York">New York</option>
        <option value="America/Chicago">Chicago</option>
        <option value="America/Los_Angeles">Los Angeles</option>
        <option value="Asia/Dubai">Dubai</option>
        <option value="Asia/Kolkata">India</option>
      </select>

      <label className="flex items-center gap-1 text-xs">
        <input
          type="checkbox"
          checked={schedule.enabled}
          onChange={(e) => onChange({ ...schedule, enabled: e.target.checked })}
          className="rounded"
        />
        <span className="text-muted-foreground">Active</span>
      </label>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

const UNTAGGED = '__untagged__';

const getTags = (w: WorkflowEntity): string[] =>
  ((w as Record<string, unknown>).tags as string[] | undefined) ?? [];

type GroupBy = 'tag' | 'trigger' | 'none';

const TRIGGER_LABELS: Record<string, string> = {
  call: 'Call Ended',
  dealerkit: 'DealerKit Lead',
  any: 'Any',
  scheduled: 'Scheduled',
  webhook: 'Webhook',
  event: 'Event',
};

// The chosen grouping is a workspace preference, not page state — it should
// survive a reload, otherwise everyone re-picks it on every visit.
const GROUP_BY_STORAGE_KEY = 'workflows.list.groupBy';

const readStoredGroupBy = (): GroupBy => {
  try {
    const stored = localStorage.getItem(GROUP_BY_STORAGE_KEY);
    return stored === 'tag' || stored === 'trigger' || stored === 'none' ? stored : 'tag';
  } catch {
    return 'tag';
  }
};

interface WorkflowGroup {
  key: string;
  label: string;
  items: WorkflowEntity[];
}

/**
 * Chip editor for a workflow's tags. Existing tags across the platform are
 * offered as a datalist — typing "New" and picking "Newcross" is what keeps the
 * groups from fragmenting into near-duplicate spellings.
 */
interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions: string[];
}

const TagInput = ({ value, onChange, suggestions }: TagInputProps) => {
  const [draft, setDraft] = useState('');

  const commit = (raw: string) => {
    const tag = raw.trim();
    if (!tag) return;
    // Case-insensitive so "newcross" never lands beside an existing "Newcross".
    if (!value.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      onChange([...value, tag]);
    }
    setDraft('');
  };

  return (
    <div className="border-input bg-background focus-within:ring-ring flex min-w-56 flex-wrap items-center gap-1 rounded-md border px-1.5 py-1 shadow-sm focus-within:ring-1">
      <Tag className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
      {value.map((tag) => (
        <span
          key={tag}
          className="bg-secondary text-secondary-foreground flex items-center gap-1 rounded px-1.5 py-0.5 text-xs"
        >
          {tag}
          <button
            type="button"
            onClick={() => onChange(value.filter((t) => t !== tag))}
            className="hover:text-destructive"
            aria-label={`Remove tag ${tag}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        list="workflow-tag-suggestions"
        value={draft}
        onChange={(e) => {
          // A comma is how most people expect to end a tag, so treat it as Enter.
          if (e.target.value.includes(',')) {
            e.target.value.split(',').forEach(commit);
            return;
          }
          setDraft(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(draft);
          } else if (e.key === 'Backspace' && !draft && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => commit(draft)}
        placeholder={value.length === 0 ? 'Tags (Enter to add)' : ''}
        className="min-w-24 flex-1 bg-transparent px-1 py-0.5 text-sm focus:outline-none"
      />
      <datalist id="workflow-tag-suggestions">
        {suggestions.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Workflow editor modal (full-screen)
// ---------------------------------------------------------------------------

interface WorkflowModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflow: WorkflowEntity | null;
  onSave: (values: {
    name: string;
    description: string;
    is_default: boolean;
    trigger_type: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
    post_call_workflow: PostCallWorkflow | null;
    schedule?: { cron: string; timezone: string; enabled: boolean } | null;
    webhook_rate_limit?: number | null;
    trigger_event?: 'sms.failed' | null;
    tags: string[];
  }) => void;
  isLoading: boolean;
  /** Every tag already in use, offered as autocomplete in the tag editor. */
  allTags: string[];
}

// Internal platform events an event-triggered workflow can subscribe to.
const TRIGGER_EVENT_OPTIONS: { value: 'sms.failed'; label: string }[] = [
  { value: 'sms.failed', label: 'SMS / WhatsApp send failed' },
];

const WorkflowModal = ({
  open,
  onOpenChange,
  workflow,
  onSave,
  isLoading,
  allTags,
}: WorkflowModalProps) => {
  const isEditing = workflow !== null;

  const { dealerKit: featureDealerKit } = useFeaturesStore();

  const [name, setName] = useState(workflow?.name ?? '');
  const [description, setDescription] = useState(workflow?.description ?? '');
  const [isDefault, setIsDefault] = useState(workflow?.is_default ?? false);
  const [triggerType, setTriggerType] = useState<
    'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event'
  >(
    (workflow?.trigger_type as 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event') ??
      'call',
  );
  const [triggerEvent, setTriggerEvent] = useState<'sms.failed'>(
    ((workflow as Record<string, unknown> | null)?.trigger_event as 'sms.failed') ?? 'sms.failed',
  );
  const [schedule, setSchedule] = useState<{
    cron: string;
    timezone: string;
    enabled: boolean;
  } | null>(workflow?.schedule ?? null);
  const [webhookRateLimit, setWebhookRateLimit] = useState<number>(
    ((workflow as Record<string, unknown> | null)?.webhook_rate_limit as number) ?? 60,
  );
  const [canvasValue, setCanvasValue] = useState<PostCallWorkflow | null>(
    (workflow?.post_call_workflow as PostCallWorkflow | null) ?? null,
  );
  const [tags, setTags] = useState<string[]>(workflow ? getTags(workflow) : []);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setName(workflow?.name ?? '');
      setDescription(workflow?.description ?? '');
      setIsDefault(workflow?.is_default ?? false);
      setTriggerType(
        (workflow?.trigger_type as
          | 'call'
          | 'dealerkit'
          | 'any'
          | 'scheduled'
          | 'webhook'
          | 'event') ?? 'call',
      );
      setTriggerEvent(
        ((workflow as Record<string, unknown> | null)?.trigger_event as 'sms.failed') ??
          'sms.failed',
      );
      setSchedule(workflow?.schedule ?? null);
      setCanvasValue((workflow?.post_call_workflow as PostCallWorkflow | null) ?? null);
      setTags(workflow ? getTags(workflow) : []);
    }
    onOpenChange(nextOpen);
  };

  const handleSave = () => {
    onSave({
      name,
      description,
      is_default: isDefault,
      trigger_type: triggerType,
      post_call_workflow: canvasValue,
      schedule: triggerType === 'scheduled' ? schedule : null,
      webhook_rate_limit: triggerType === 'webhook' ? webhookRateLimit : null,
      trigger_event: triggerType === 'event' ? triggerEvent : null,
      tags,
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60" />
        <Dialog.Content className="bg-background fixed inset-0 z-50 flex flex-col">
          {/* Header — single compact strip */}
          <div className="flex items-center gap-3 border-b px-4 py-2">
            <Dialog.Title className="text-sm font-semibold whitespace-nowrap">
              {isEditing ? 'Edit Workflow' : 'New Workflow'}
            </Dialog.Title>

            <input
              className="border-input bg-background focus-visible:ring-ring w-48 rounded-md border px-2 py-1 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Workflow name *"
            />

            <input
              className="border-input bg-background focus-visible:ring-ring w-56 rounded-md border px-2 py-1 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description (optional)"
            />

            <TagInput value={tags} onChange={setTags} suggestions={allTags} />

            <label className="flex cursor-pointer items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded"
              />
              <span className="text-muted-foreground whitespace-nowrap">Default</span>
            </label>

            <select
              value={triggerType}
              onChange={(e) => {
                const val = e.target.value as
                  | 'call'
                  | 'dealerkit'
                  | 'any'
                  | 'scheduled'
                  | 'webhook'
                  | 'event';
                setTriggerType(val);
                if (val === 'scheduled' && !schedule) {
                  setSchedule({ cron: '0 10 * * *', timezone: 'Europe/London', enabled: true });
                }
              }}
              className="border-input bg-background focus-visible:ring-ring rounded-md border px-2 py-1 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
            >
              <option value="call">Call Ended</option>
              {featureDealerKit && <option value="dealerkit">DealerKit Lead</option>}
              {featureDealerKit && <option value="any">Any</option>}
              <option value="scheduled">Scheduled</option>
              <option value="webhook">Webhook</option>
              <option value="event">Event</option>
            </select>

            {triggerType === 'event' && (
              <select
                value={triggerEvent}
                onChange={(e) => setTriggerEvent(e.target.value as 'sms.failed')}
                className="border-input bg-background focus-visible:ring-ring rounded-md border px-2 py-1 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
              >
                {TRIGGER_EVENT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}

            {triggerType === 'scheduled' && schedule && (
              <ScheduleConfig schedule={schedule} onChange={setSchedule} />
            )}

            {triggerType === 'webhook' && (
              <WebhookConfig
                workflowId={workflow?.id}
                webhookRateLimit={webhookRateLimit}
                onRateLimitChange={setWebhookRateLimit}
                hasToken={!!(workflow as Record<string, unknown> | null)?.webhook_token_hash}
              />
            )}

            <div className="flex-1" />

            <Button size="sm" onClick={handleSave} disabled={isLoading || !name.trim()}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
            <Dialog.Close asChild>
              <Button variant="ghost" size="sm">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {/* Canvas */}
          <div className="flex-1 overflow-hidden">
            <WorkflowCanvas
              value={canvasValue}
              onChange={setCanvasValue}
              triggerType={triggerType}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const WorkflowsPage = () => {
  usePageTitle('Workflows');
  const navigate = useNavigate();

  const { data: workflows = [], isLoading, error } = useWorkflows();
  const createMutation = useCreateWorkflow();
  const updateMutation = useUpdateWorkflow();
  const deleteMutation = useDeleteWorkflow();
  const runMutation = useRunWorkflow();

  const [editingWorkflow, setEditingWorkflow] = useState<WorkflowEntity | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkflowEntity | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { toast } = useToast();

  // ------ search / filter / grouping ------

  const [search, setSearch] = useState('');
  const [triggerFilter, setTriggerFilter] = useState<string>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<GroupBy>(readStoredGroupBy);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());

  const changeGroupBy = (next: GroupBy) => {
    setGroupBy(next);
    try {
      localStorage.setItem(GROUP_BY_STORAGE_KEY, next);
    } catch {
      // Private-mode storage failures must not break the page.
    }
  };

  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleTag = (tag: string) =>
    setSelectedTags((prev) =>
      prev.some((t) => t.toLowerCase() === tag.toLowerCase())
        ? prev.filter((t) => t.toLowerCase() !== tag.toLowerCase())
        : [...prev, tag],
    );

  // Every tag in use, with how many workflows carry it. Keyed case-insensitively
  // so one bucket survives inconsistent spellings across workflows.
  const tagCounts = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    for (const w of workflows) {
      for (const tag of getTags(w)) {
        const key = tag.toLowerCase();
        const entry = counts.get(key);
        if (entry) entry.count += 1;
        else counts.set(key, { label: tag, count: 1 });
      }
    }
    return [...counts.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [workflows]);

  const allTags = useMemo(() => tagCounts.map((t) => t.label), [tagCounts]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return workflows.filter((w) => {
      if (triggerFilter !== 'all' && (w.trigger_type ?? 'call') !== triggerFilter) return false;

      const tags = getTags(w);
      // AND across selected tags: each chip narrows further, the way descending
      // into a nested folder would.
      if (
        selectedTags.length > 0 &&
        !selectedTags.every((sel) => tags.some((t) => t.toLowerCase() === sel.toLowerCase()))
      ) {
        return false;
      }

      if (!query) return true;
      return (
        w.name.toLowerCase().includes(query) ||
        (w.description ?? '').toLowerCase().includes(query) ||
        tags.some((t) => t.toLowerCase().includes(query))
      );
    });
  }, [workflows, search, triggerFilter, selectedTags]);

  const groups = useMemo<WorkflowGroup[]>(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', label: 'All Workflows', items: filtered }];
    }

    if (groupBy === 'trigger') {
      const byTrigger = new Map<string, WorkflowEntity[]>();
      for (const w of filtered) {
        const key = w.trigger_type ?? 'call';
        const bucket = byTrigger.get(key);
        if (bucket) bucket.push(w);
        else byTrigger.set(key, [w]);
      }
      return [...byTrigger.entries()]
        .map(([key, items]) => ({ key, label: TRIGGER_LABELS[key] ?? key, items }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    // Group by tag. A workflow with several tags appears under each of them —
    // that multi-membership is the point of tags over folders.
    const byTag = new Map<string, WorkflowGroup>();
    const untagged: WorkflowEntity[] = [];
    for (const w of filtered) {
      const tags = getTags(w);
      if (tags.length === 0) {
        untagged.push(w);
        continue;
      }
      for (const tag of tags) {
        const key = tag.toLowerCase();
        const group = byTag.get(key);
        if (group) group.items.push(w);
        else byTag.set(key, { key, label: tag, items: [w] });
      }
    }

    const sorted = [...byTag.values()].sort((a, b) => a.label.localeCompare(b.label));
    if (untagged.length > 0) {
      sorted.push({ key: UNTAGGED, label: 'Untagged', items: untagged });
    }
    return sorted;
  }, [filtered, groupBy]);

  const hasFilters = search.trim() !== '' || triggerFilter !== 'all' || selectedTags.length > 0;

  const clearFilters = () => {
    setSearch('');
    setTriggerFilter('all');
    setSelectedTags([]);
  };

  // ------ open/close modal ------

  const handleNew = () => {
    setEditingWorkflow(null);
    setIsModalOpen(true);
  };

  const handleEdit = (w: WorkflowEntity) => {
    setEditingWorkflow(w);
    setIsModalOpen(true);
  };

  const handleModalOpenChange = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setEditingWorkflow(null);
    }
  };

  // ------ save ------

  const handleSave = async (values: {
    name: string;
    description: string;
    is_default: boolean;
    trigger_type: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
    post_call_workflow: PostCallWorkflow | null;
    schedule?: { cron: string; timezone: string; enabled: boolean } | null;
    webhook_rate_limit?: number | null;
    trigger_event?: 'sms.failed' | null;
    tags: string[];
  }) => {
    try {
      setIsSaving(true);

      const payload = {
        name: values.name,
        description: values.description || null,
        is_default: values.is_default,
        trigger_type: values.trigger_type,
        post_call_workflow: values.post_call_workflow ?? undefined,
        schedule: values.schedule ?? undefined,
        webhook_rate_limit: values.webhook_rate_limit ?? undefined,
        trigger_event: values.trigger_event,
        tags: values.tags,
      };

      if (editingWorkflow) {
        await updateMutation.mutateAsync({ id: editingWorkflow.id, data: payload });
        toast({
          variant: 'success',
          title: 'Workflow updated',
          description: `"${values.name}" has been updated successfully.`,
        });
      } else {
        await createMutation.mutateAsync(payload);
        toast({
          variant: 'success',
          title: 'Workflow created',
          description: `"${values.name}" has been created successfully.`,
        });
      }

      // Stay in the editor — only X button closes the modal
      // After creating, store the new workflow as "editing" so subsequent saves are updates
      if (!editingWorkflow) {
        // Refetch will update the list; we stay in modal
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: editingWorkflow ? 'Failed to update workflow' : 'Failed to create workflow',
        description: extractErrorMessage(err),
      });
    } finally {
      setIsSaving(false);
    }
  };

  // ------ delete ------

  const handleDelete = (w: WorkflowEntity) => {
    setDeleteTarget(w);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      toast({
        variant: 'success',
        title: 'Workflow deleted',
        description: `"${deleteTarget.name}" has been deleted.`,
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete workflow',
        description: extractErrorMessage(err),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // ------ render ------

  const renderWorkflow = (w: WorkflowEntity) => {
    const nodeCount = (w.post_call_workflow as PostCallWorkflow | null)?.nodes?.length ?? 0;
    const tags = getTags(w);
    return (
      <div key={w.id} className="rounded-lg border p-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-lg font-semibold">{w.name}</p>
              {w.is_default && (
                <Badge variant="default" className="gap-1">
                  <Star className="h-3 w-3" />
                  Default
                </Badge>
              )}
              <Badge variant="secondary">
                {nodeCount} node{nodeCount !== 1 ? 's' : ''}
              </Badge>
              {w.trigger_type &&
                w.trigger_type !== 'call' &&
                w.trigger_type !== 'scheduled' &&
                w.trigger_type !== 'event' && (
                  <Badge variant="outline" className="capitalize">
                    {w.trigger_type === 'dealerkit' ? 'DealerKit' : w.trigger_type}
                  </Badge>
                )}
              {w.trigger_type === 'event' && (
                <Badge variant="outline" className="gap-1">
                  On {(w as Record<string, unknown>).trigger_event as string}
                </Badge>
              )}
              {w.trigger_type === 'scheduled' && (
                <Badge variant="outline" className="gap-1">
                  <Clock className="h-3 w-3" />
                  {(w.schedule as { cron?: string } | null)?.cron
                    ? describeCron((w.schedule as { cron: string }).cron)
                    : 'Scheduled'}
                </Badge>
              )}
              {w.trigger_type === 'webhook' && (
                <Badge variant="outline" className="gap-1">
                  Webhook
                </Badge>
              )}
              {/* Clicking a tag filters to it, so the badges double as navigation. */}
              {tags.map((tag) => (
                <button key={tag} type="button" onClick={() => toggleTag(tag)}>
                  <Badge variant="secondary" className="cursor-pointer gap-1">
                    <Tag className="h-3 w-3" />
                    {tag}
                  </Badge>
                </button>
              ))}
            </div>
            <p className="text-muted-foreground mt-0.5 text-sm">
              {w.description ? <>{w.description} &middot; </> : null}
              Created {formatDate(w.created_at)}
            </p>
          </div>
          <div className="flex gap-2">
            {w.trigger_type === 'scheduled' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  runMutation.mutate(w.id, {
                    onSuccess: () =>
                      toast({
                        variant: 'success',
                        title: 'Workflow queued',
                        description: `"${w.name}" has been queued for execution.`,
                      }),
                    onError: (err) =>
                      toast({
                        variant: 'destructive',
                        title: 'Failed to run workflow',
                        description: extractErrorMessage(err),
                      }),
                  });
                }}
                disabled={runMutation.isPending}
              >
                <Play className="h-4 w-4" />
                Run Now
              </Button>
            )}
            {w.trigger_type === 'webhook' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/workflows/${w.id}/batch-runs`)}
              >
                <Database className="h-4 w-4" />
                Batch Runs
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => handleEdit(w)}>
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
            <Button variant="destructive" size="sm" onClick={() => handleDelete(w)}>
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <GitBranch className="text-destructive mx-auto h-12 w-12 opacity-50" />
          <p className="text-destructive mt-4 font-medium">Failed to load workflows</p>
          <p className="text-muted-foreground mt-2 text-sm">{extractErrorMessage(error)}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading workflows...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page header */}
      <header className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Automation</p>
          <h1 className="text-3xl font-bold tracking-tight">Workflows</h1>
          <p className="text-muted-foreground max-w-3xl">
            Create and manage post-call workflows that run automatically after each call ends.
          </p>
        </div>
        <Button onClick={handleNew}>
          <Plus className="h-4 w-4" />
          New Workflow
        </Button>
      </header>

      {/* Toolbar — search, trigger filter, grouping */}
      <Card>
        <CardContent className="space-y-3 pt-6">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-56 flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search workflows by name, description or tag..."
                className="pl-9"
              />
            </div>

            <select
              value={triggerFilter}
              onChange={(e) => setTriggerFilter(e.target.value)}
              className="border-input bg-background focus-visible:ring-ring h-10 rounded-md border px-3 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
              aria-label="Filter by trigger"
            >
              <option value="all">All triggers</option>
              {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <select
              value={groupBy}
              onChange={(e) => changeGroupBy(e.target.value as GroupBy)}
              className="border-input bg-background focus-visible:ring-ring h-10 rounded-md border px-3 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
              aria-label="Group by"
            >
              <option value="tag">Group by tag</option>
              <option value="trigger">Group by trigger</option>
              <option value="none">No grouping</option>
            </select>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>

          {tagCounts.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <Tag className="text-muted-foreground h-3.5 w-3.5" />
              {tagCounts.map(({ label, count }) => {
                const active = selectedTags.some((t) => t.toLowerCase() === label.toLowerCase());
                return (
                  <button key={label} type="button" onClick={() => toggleTag(label)}>
                    <Badge
                      variant={active ? 'default' : 'outline'}
                      className="cursor-pointer gap-1"
                    >
                      {label}
                      <span className="opacity-60">{count}</span>
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* List */}
      {workflows.length === 0 ? (
        <Card>
          <CardContent>
            <div className="text-muted-foreground flex min-h-[200px] flex-col items-center justify-center space-y-4 py-8 text-center">
              <GitBranch className="h-12 w-12 opacity-50" />
              <div>
                <p className="font-medium">No workflows yet</p>
                <p className="text-sm">Create your first post-call workflow.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent>
            <div className="text-muted-foreground flex min-h-[200px] flex-col items-center justify-center space-y-4 py-8 text-center">
              <Search className="h-12 w-12 opacity-50" />
              <div>
                <p className="font-medium">No workflows match your filters</p>
                <p className="text-sm">Try a different search term or clear the filters.</p>
              </div>
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const isCollapsed = collapsedGroups.has(group.key);
            return (
              <Card key={group.key}>
                <CardHeader className="py-3">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    className="flex w-full items-center gap-2 text-left"
                    aria-expanded={!isCollapsed}
                  >
                    {isCollapsed ? (
                      <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
                    ) : (
                      <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
                    )}
                    <CardTitle className="text-base">
                      {group.key === UNTAGGED ? (
                        <span className="text-muted-foreground">{group.label}</span>
                      ) : (
                        group.label
                      )}
                    </CardTitle>
                    <Badge variant="secondary">{group.items.length}</Badge>
                  </button>
                </CardHeader>
                {!isCollapsed && (
                  <CardContent>
                    <div className="space-y-4">{group.items.map(renderWorkflow)}</div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit modal — key forces remount so state resets on workflow change */}
      <WorkflowModal
        key={editingWorkflow?.id ?? 'new'}
        open={isModalOpen}
        onOpenChange={handleModalOpenChange}
        workflow={editingWorkflow}
        onSave={handleSave}
        isLoading={isSaving}
        allTags={allTags}
      />

      {/* Delete confirmation */}
      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        resourceName="workflow"
        resourceIdentifier={deleteTarget?.name}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};
